import type { ThemeColor } from "@/colors/index";

/**
 * AI-196: Project icons organized by category
 * ~60 curated Lucide icons for project customization
 */
export const PROJECT_ICON_CATEGORIES = [
  {
    label: "General",
    icons: [
      { value: "folder", label: "Folder" },
      { value: "folder-open", label: "Folder Open" },
      { value: "home", label: "Home" },
      { value: "star", label: "Star" },
      { value: "heart", label: "Heart" },
      { value: "bookmark", label: "Bookmark" },
      { value: "flag", label: "Flag" },
      { value: "tag", label: "Tag" },
      { value: "archive", label: "Archive" },
      { value: "box", label: "Box" },
    ],
  },
  {
    label: "Business",
    icons: [
      { value: "briefcase", label: "Briefcase" },
      { value: "building-2", label: "Building" },
      { value: "landmark", label: "Landmark" },
      { value: "banknote", label: "Banknote" },
      { value: "wallet", label: "Wallet" },
      { value: "credit-card", label: "Credit Card" },
      { value: "receipt", label: "Receipt" },
      { value: "pie-chart", label: "Pie Chart" },
      { value: "bar-chart-2", label: "Bar Chart" },
      { value: "trending-up", label: "Trending Up" },
    ],
  },
  {
    label: "Technology",
    icons: [
      { value: "cpu", label: "CPU" },
      { value: "terminal", label: "Terminal" },
      { value: "code", label: "Code" },
      { value: "code-2", label: "Code 2" },
      { value: "database", label: "Database" },
      { value: "server", label: "Server" },
      { value: "cloud", label: "Cloud" },
      { value: "wifi", label: "WiFi" },
      { value: "smartphone", label: "Smartphone" },
      { value: "laptop", label: "Laptop" },
    ],
  },
  {
    label: "Communication",
    icons: [
      { value: "message-circle", label: "Message" },
      { value: "message-square", label: "Chat" },
      { value: "mail", label: "Mail" },
      { value: "phone", label: "Phone" },
      { value: "video", label: "Video" },
      { value: "headphones", label: "Headphones" },
      { value: "mic", label: "Mic" },
      { value: "bell", label: "Bell" },
      { value: "megaphone", label: "Megaphone" },
      { value: "radio", label: "Radio" },
    ],
  },
  {
    label: "Creative",
    icons: [
      { value: "palette", label: "Palette" },
      { value: "brush", label: "Brush" },
      { value: "pen-tool", label: "Pen" },
      { value: "pencil", label: "Pencil" },
      { value: "image", label: "Image" },
      { value: "camera", label: "Camera" },
      { value: "film", label: "Film" },
      { value: "music", label: "Music" },
      { value: "gamepad-2", label: "Gamepad" },
      { value: "shapes", label: "Shapes" },
    ],
  },
  {
    label: "Science",
    icons: [
      { value: "flask-conical", label: "Flask" },
      { value: "atom", label: "Atom" },
      { value: "microscope", label: "Microscope" },
      { value: "dna", label: "DNA" },
      { value: "brain", label: "Brain" },
      { value: "lightbulb", label: "Lightbulb" },
      { value: "rocket", label: "Rocket" },
      { value: "globe", label: "Globe" },
      { value: "compass", label: "Compass" },
      { value: "map", label: "Map" },
    ],
  },
  {
    label: "Nature",
    icons: [
      { value: "lab/bee", label: "Bee" },
      { value: "lab/butterfly", label: "Butterfly" },
      { value: "lab/whale", label: "Whale" },
      { value: "lab/owl", label: "Owl" },
      { value: "lab/penguin", label: "Penguin" },
      { value: "lab/cactus", label: "Cactus" },
      { value: "lab/flower-lotus", label: "Lotus" },
      { value: "lab/flower-tulip", label: "Tulip" },
      { value: "lab/flower-rose-single", label: "Rose" },
      { value: "lab/trees-forest", label: "Forest" },
    ],
  },
  {
    label: "Food & Drink",
    icons: [
      { value: "lab/burger", label: "Burger" },
      { value: "lab/cocktail", label: "Cocktail" },
      { value: "lab/coffee-bean", label: "Coffee" },
      { value: "lab/sushi-chopsticks", label: "Sushi" },
      { value: "lab/pie", label: "Pie" },
      { value: "lab/avocado", label: "Avocado" },
      { value: "lab/lemon", label: "Lemon" },
      { value: "lab/strawberry", label: "Strawberry" },
      { value: "lab/grapes", label: "Grapes" },
      { value: "lab/wine-glass-bottle", label: "Wine" },
    ],
  },
  {
    label: "Sports",
    icons: [
      { value: "lab/soccer-ball", label: "Soccer" },
      { value: "lab/basketball", label: "Basketball" },
      { value: "lab/baseball", label: "Baseball" },
      { value: "lab/football", label: "Football" },
      { value: "lab/tennis-racket", label: "Tennis" },
      { value: "lab/volleyball", label: "Volleyball" },
      { value: "lab/bowling", label: "Bowling" },
      { value: "lab/surfboard", label: "Surfboard" },
      { value: "lab/snowboard", label: "Snowboard" },
      { value: "lab/target-arrow", label: "Target" },
    ],
  },
  {
    label: "Objects",
    icons: [
      { value: "lab/gem-ring", label: "Ring" },
      { value: "lab/candlestick-lit", label: "Candle" },
      { value: "lab/chest", label: "Chest" },
      { value: "lab/toolbox", label: "Toolbox" },
      { value: "lab/bell-concierge", label: "Concierge" },
      { value: "lab/steering-wheel", label: "Wheel" },
      { value: "lab/ufo", label: "UFO" },
      { value: "lab/windmill", label: "Windmill" },
      { value: "lab/planet", label: "Planet" },
      { value: "lab/peace", label: "Peace" },
    ],
  },
] as const;

// Type derived from all icon values across all categories
export type ProjectIcon =
  (typeof PROJECT_ICON_CATEGORIES)[number]["icons"][number]["value"];

/**
 * AI-196: Theme colors available for project customization
 * Excludes gray variants (gray, mauve, slate, sage, olive, sand)
 */
export const PROJECT_COLORS: ThemeColor[] = [
  "brand",
  "tomato",
  "red",
  "ruby",
  "crimson",
  "pink",
  "plum",
  "purple",
  "violet",
  "iris",
  "indigo",
  "blue",
  "cyan",
  "teal",
  "jade",
  "green",
  "grass",
  "brown",
  "bronze",
  "gold",
  "sky",
  "mint",
  "lime",
  "yellow",
  "amber",
  "orange",
];

export const DEFAULT_PROJECT_ICON: ProjectIcon = "folder";
export const DEFAULT_PROJECT_COLOR: ThemeColor = "brand";

/**
 * Resolve a stored icon name to its full Iconify icon string.
 * Standard lucide icons: "folder" → "lucide:folder"
 * Lab icons: "lab/bee" → "lucide-lab:bee"
 */
export function resolveProjectIcon(name: string): string {
  if (name.startsWith("lab/")) {
    return `lucide-lab:${name.slice(4)}`;
  }
  return `lucide:${name}`;
}
